import React, { useEffect, useState } from "react";
import { GiPathDistance } from "react-icons/gi";
import { IoTimerOutline } from "react-icons/io5";
import { Timeline } from "primereact/timeline";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../App.css";
import GeoImages from "../Constant/GeoImages";
import { CiCalendar } from "react-icons/ci";
import { BsPersonCircle } from "react-icons/bs";
import { IoShareSocialSharp } from "react-icons/io5";
import { IoBatteryHalfOutline } from "react-icons/io5";
import { MdDownload } from "react-icons/md";
import { googleMapsApiKey } from "../../config/clientsConfig";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { showSuccessToast } from "../../components/Constant/Notification";

const LiveUserTimeline = ({ data = {}, userName, onPointClick, selectedProperty }) => {
  const [locationNames, setLocationNames] = useState(new Map());
  const [geocodingLoading, setGeocodingLoading] = useState(new Set());
  const [activeTab, setActiveTab] = useState('timeline');

  const getLocationName = async (lat, lng, pointId) => {
    if (geocodingLoading.has(pointId) || locationNames.has(pointId)) return;

    setGeocodingLoading((prev) => new Set([...prev, pointId]));

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`
      );
      const geocodeData = await response.json();

      if (geocodeData.status === "OK" && geocodeData.results.length > 0) {
        const result = geocodeData.results[0];
        const address = result.formatted_address;
        const addressComponents = result.address_components;
        let shortAddress = "";
        let area = "";
        let city = "";

        for (const component of addressComponents) {
          if (component.types.includes("sublocality_level_1") || component.types.includes("locality")) {
            if (!area) area = component.long_name;
          }
          if (
            component.types.includes("administrative_area_level_2") ||
            component.types.includes("administrative_area_level_1")
          ) {
            if (!city) city = component.long_name;
          }
        }

        shortAddress = area && city ? `${area}, ${city}` : area || city || address.split(",").slice(0, 2).join(",").trim();

        setLocationNames((prev) => new Map(prev.set(pointId, {
          fullAddress: address,
          shortAddress,
          area,
          city,
        })));
      } else {
        setLocationNames((prev) => new Map(prev.set(pointId, {
          fullAddress: "Unknown Location",
          shortAddress: "Unknown Location",
          area: "",
          city: "",
        })));
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
      setLocationNames((prev) => new Map(prev.set(pointId, {
        fullAddress: "Location not available",
        shortAddress: "Location not available",
        area: "",
        city: "",
      })));
    } finally {
      setGeocodingLoading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(pointId);
        return newSet;
      });
    }
  };

  const timelineData = (data.tripDetails || [])
    .filter((trip) => {
      if (!selectedProperty) return true; 
      return trip.propertyId === selectedProperty?.value;
    })
    .map((trip, index) => {
      if (!trip.geoPoint || trip.geoPoint.length !== 2) {
        return {
          time: trip.time || "",
          action: trip.message || "",
          propertyName: trip.propertyName || "",
          duration: trip.duration || "",
          lat: null,
          lng: null,
          pointId: `timeline-${index}`,
          coordinates: "N/A",
          index,
        };
      }
      const pointId = `timeline-${index}-${trip.geoPoint[0]}-${trip.geoPoint[1]}`;
      return {
        time: trip.time || "",
        action: trip.message || "",
        propertyName: trip.propertyName || "",
        duration: trip.duration || "",
        lat: parseFloat(trip.geoPoint[0]),
        lng: parseFloat(trip.geoPoint[1]),
        pointId,
        coordinates: `${trip.geoPoint[0]}, ${trip.geoPoint[1]}`,
        index,
      };
    });

  useEffect(() => {
    timelineData.forEach((item) => {
      if (item.lat && item.lng && !locationNames.has(item.pointId)) {
        getLocationName(item.lat, item.lng, item.pointId);
      }
    });
  }, [timelineData]);

  const customizedOpposite = (item) => (
    <div className="text-customGreen text-nowrap font-medium text-[13px] mr-3">{item.time}</div>
  );

  const handlePointClick = (point) => {
    if (onPointClick && point.lat && point.lng) {
      const mapPoint = {
        lat: point.lat,
        lng: point.lng,
        message: point.action,
        time: point.time,
        duration: point.duration,
        index: point.index,
        id: point.pointId,
        isStart: point.action.toLowerCase().trim() === "trip started",
        isEnd: point.action.toLowerCase().trim() === "trip ended",
        isStop: point.action.toLowerCase().trim() === "stop",
        isDrive: point.action.toLowerCase().trim() === "drive",
      };
      onPointClick(mapPoint);
    }
  };

  const customizedContent = (item) => {
    const locationData = locationNames.get(item.pointId);
    const isLoadingLocation = geocodingLoading.has(item.pointId);

    return (
      <div
        className="bg-gray-50 w-[270px] rounded-lg px-4 py-3 mb-5 shadow-sm cursor-pointer hover:bg-blue-50 hover:shadow-md transition-all duration-200"
        onClick={() => handlePointClick(item)}
        title="Click to view on map"
      >
        <div className="flex justify-between items-center mb-2">
          <p className="text-[14px] text-gray-800 font-medium capitalize">{item.action}</p>
          {item.duration && (
            <span className="text-xs text-blue-500 bg-blue-50 px-2 py-1 rounded">{item.duration}</span>
          )}
        </div>

        <div className="mt-2">
          <div className="flex items-start gap-1">
            <span className="text-gray-400 text-xs mt-1">📍</span>
            <div className="flex-1">
              {isLoadingLocation ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs text-gray-500 italic">Loading location...</span>
                </div>
              ) : locationData && locationData.shortAddress !== "Unknown Location" ? (
                <div>
                  <p className="text-xs text-gray-700 font-medium leading-relaxed">
                    {locationData.shortAddress}
                  </p>
                  {locationData.fullAddress !== locationData.shortAddress && (
                    <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">
                      {locationData.fullAddress.length > 60
                        ? `${locationData.fullAddress.substring(0, 60)}...`
                        : locationData.fullAddress}
                    </p>
                  )}
                  {item.propertyName && (
                    <p className="text-[12px] text-customGreen mt-1 leading-relaxed font-semibold">
                      Property: {item.propertyName}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-xs text-gray-600 font-medium">Coordinates:</p>
                  <p className="text-[10px] text-gray-500 font-mono">{item.coordinates}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end mt-2">
          <span className="text-xs text-blue-500 opacity-70">🗺️ Click to view on map</span>
        </div>
      </div>
    );
  };

  const downloadTimelineReport = async () => {
    const rows = timelineData.map(item => {
      const locationData = locationNames.get(item.pointId);
      const location = locationData?.shortAddress || "Loading...";
      return {
        "Time": item.time,
        "Action": item.action,
        "Property Name": item.propertyName || "N/A",
        "Duration": item.duration || "N/A",
        "Location": location,
        "Coordinates": item.coordinates
      };
    });
  
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Timeline Report");
  
    const headers = Object.keys(rows[0] || {});
    worksheet.columns = headers.map(header => ({
      header: header,
      key: header,
      width: 25
    }));
  
    const todaysDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  
    const dateInfoText = `Timeline Report | Generated on: ${todaysDate}`;
  
    worksheet.insertRow(1);
    worksheet.mergeCells(1, 1, 1, headers.length);
    const dateCell = worksheet.getCell(1, 1);
    dateCell.value = dateInfoText;
    dateCell.font = { bold: true, size: 12, color: { argb: "FF000000" } };
    dateCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6E6" } };
    dateCell.alignment = { vertical: "middle", horizontal: "center" };
    worksheet.getRow(1).height = 40;
  
    // Header style
    worksheet.getRow(2).eachCell(cell => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "228B22" } }; // green header
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
    });
  
    rows.forEach(item => worksheet.addRow(item));
  
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 2) {
        row.height = 25;
        row.eachCell(cell => {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
        });
      }
    });
  
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
  
    saveAs(blob, `${userName || "User"}_Timeline_Report_${todaysDate}.xlsx`);
  };

  // Get detailed property data with all actions and times
  const getDetailedPropertyData = () => {
    const propertyGroups = timelineData.reduce((acc, item) => {
      if (item.propertyName && item.propertyName.trim() !== '') {
        if (!acc[item.propertyName]) {
          acc[item.propertyName] = {
            propertyName: item.propertyName,
            activities: [],
            totalVisits: 0,
            firstVisit: null,
            lastVisit: null,
            totalTimeSpent: 0
          };
        }
        
        acc[item.propertyName].activities.push({
          time: item.time,
          action: item.action,
          duration: item.duration,
          coordinates: item.coordinates,
          locationData: locationNames.get(item.pointId),
          pointId: item.pointId
        });
        
        acc[item.propertyName].totalVisits++;
        
        // Set first and last visit times using proper time comparison
        const currentTime = new Date(`1970/01/01 ${item.time}`);
        const firstTime = acc[item.propertyName].firstVisit ? new Date(`1970/01/01 ${acc[item.propertyName].firstVisit}`) : null;
        const lastTime = acc[item.propertyName].lastVisit ? new Date(`1970/01/01 ${acc[item.propertyName].lastVisit}`) : null;
        
        if (!firstTime || currentTime < firstTime) {
          acc[item.propertyName].firstVisit = item.time;
        }
        if (!lastTime || currentTime > lastTime) {
          acc[item.propertyName].lastVisit = item.time;
        }
      }
      return acc;
    }, {});

    // Sort activities by time for each property and calculate total time spent
    Object.values(propertyGroups).forEach(property => {
      property.activities.sort((a, b) => {
        const timeA = new Date(`1970/01/01 ${a.time}`);
        const timeB = new Date(`1970/01/01 ${b.time}`);
        return timeA - timeB;
      });
      
      // Calculate total time spent at property
      if (property.activities.length > 1) {
        const firstActivity = property.activities[0];
        const lastActivity = property.activities[property.activities.length - 1];
        const firstTime = new Date(`1970/01/01 ${firstActivity.time}`);
        const lastTime = new Date(`1970/01/01 ${lastActivity.time}`);
        const timeDiff = (lastTime - firstTime) / (1000 * 60); // minutes
        
        if (timeDiff > 0) {
          const hours = Math.floor(timeDiff / 60);
          const minutes = Math.round(timeDiff % 60);
          property.totalTimeSpent = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        } else {
          property.totalTimeSpent = '< 1m';
        }
      } else {
        property.totalTimeSpent = 'N/A';
      }
    });

    return Object.values(propertyGroups);
  };



 
  // Get property summary data with pending times
  const getPropertySummaryData = () => {
    const propertyGroups = timelineData.reduce((acc, item) => {
      if (item.propertyName && item.propertyName.trim() !== '') {
        if (!acc[item.propertyName]) {
          acc[item.propertyName] = {
            propertyName: item.propertyName,
            activities: [],
            firstVisit: null,
            lastVisit: null,
            pendingTime: 'N/A',
            status: 'Unknown',
            location: null,
            coordinates: null
          };
        }
        
        acc[item.propertyName].activities.push({
          time: item.time,
          action: item.action,
          duration: item.duration,
          coordinates: item.coordinates,
          locationData: locationNames.get(item.pointId),
          pointId: item.pointId
        });
        
        // Set location and coordinates from first activity
        if (!acc[item.propertyName].location && item.coordinates !== 'N/A') {
          acc[item.propertyName].coordinates = item.coordinates;
          acc[item.propertyName].location = locationNames.get(item.pointId);
        }
        
        // Set first and last visit times
        const currentTime = new Date(`1970/01/01 ${item.time}`);
        const firstTime = acc[item.propertyName].firstVisit ? new Date(`1970/01/01 ${acc[item.propertyName].firstVisit}`) : null;
        const lastTime = acc[item.propertyName].lastVisit ? new Date(`1970/01/01 ${acc[item.propertyName].lastVisit}`) : null;
        
        if (!firstTime || currentTime < firstTime) {
          acc[item.propertyName].firstVisit = item.time;
        }
        if (!lastTime || currentTime > lastTime) {
          acc[item.propertyName].lastVisit = item.time;
        }
      }
      return acc;
    }, {});

    // Calculate pending time and status for each property
    Object.values(propertyGroups).forEach(property => {
      property.activities.sort((a, b) => {
        const timeA = new Date(`1970/01/01 ${a.time}`);
        const timeB = new Date(`1970/01/01 ${b.time}`);
        return timeA - timeB;
      });
      
      // Determine current status and pending time
      const lastActivity = property.activities[property.activities.length - 1];
      const lastActionLower = lastActivity?.action.toLowerCase() || '';
      
      // Calculate pending time from last activity to current time
      if (lastActivity) {
        const lastActivityTime = new Date(`1970/01/01 ${lastActivity.time}`);
        const currentTime = new Date();
        const currentTimeToday = new Date(`1970/01/01 ${currentTime.getHours()}:${currentTime.getMinutes().toString().padStart(2, '0')}`);
        
        const timeDiff = (currentTimeToday - lastActivityTime) / (1000 * 60); // minutes
        
        if (timeDiff > 0) {
          const hours = Math.floor(timeDiff / 60);
          const minutes = Math.round(timeDiff % 60);
          property.pendingTime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        } else {
          property.pendingTime = '< 1m';
        }
        
        // Determine status based on last action
        if (lastActionLower.includes('start') || lastActionLower.includes('arrive')) {
          property.status = 'In Progress';
        } else if (lastActionLower.includes('stop') || lastActionLower.includes('end')) {
          property.status = 'Completed';
        } else if (lastActionLower.includes('drive') || lastActionLower.includes('moving')) {
          property.status = 'En Route';
        } else {
          property.status = 'Pending';
        }
      }
    });

    return Object.values(propertyGroups);
  };

  // Enhanced download property report for summary
  const downloadPropertySummaryReport = async () => {
    const propertySummary = getPropertySummaryData();
  
    // Prepare rows for Excel
    const rows = propertySummary.map(property => ({
      "Property Name": property.propertyName,
      // "Status": property.status,
      "First Visit": property.firstVisit || "N/A",
      "Last Visit": property.lastVisit || "N/A",
      // "Total Activities": property.activities.length,
      "Location": property.location?.shortAddress || "Loading...",
      "Coordinates": property.coordinates || "N/A"
    }));
  
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Property Summary");
  
    // Define headers dynamically
    const headers = Object.keys(rows[0] || {});
    worksheet.columns = headers.map(header => ({
      header: header,
      key: header,
      width: 25
    }));
  
    // Date info row
    const todaysDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  
    const dateInfoText = `Property Summary Report | Generated on: ${todaysDate}`;
  
    worksheet.insertRow(1);
    worksheet.mergeCells(1, 1, 1, headers.length);
    const dateCell = worksheet.getCell(1, 1);
    dateCell.value = dateInfoText;
    dateCell.font = { bold: true, size: 12, color: { argb: "FF000000" } };
    dateCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E6E6" } };
    dateCell.alignment = { vertical: "middle", horizontal: "center" };
    worksheet.getRow(1).height = 40;
  
    // Style header row
    worksheet.getRow(2).eachCell(cell => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1F4E78" } }; // dark blue
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
    });
  
    // Add property summary rows
    rows.forEach(item => worksheet.addRow(item));
  
    // Format body rows
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 2) {
        row.height = 25;
        row.eachCell(cell => {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          };
        });
      }
    });
  
    // Export Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
  
    saveAs(blob, `${userName || "User"}_Property_Summary_Report_${todaysDate}.xlsx`);
  };
  
  
  const generateMultiPinLink = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return "";
  
    // Base URL
    let url = "https://www.google.com/maps/dir/?api=1";
  
    // Join waypoints
    const waypoints = coordinates.map(c => `${c.lat},${c.lng}`).join("/");
  
    url += `&origin=${coordinates[0].lat},${coordinates[0].lng}`;
    url += `&destination=${coordinates[coordinates.length - 1].lat},${coordinates[coordinates.length - 1].lng}`;
    
    if (coordinates.length > 2) {
      const midPoints = coordinates.slice(1, -1).map(c => `${c.lat},${c.lng}`).join("|");
      url += `&waypoints=${midPoints}&travelmode=driving`;
    }
  
    return url;
  };

  const handleShare = () => {
    const coords = timelineData
      .filter(item => item.lat && item.lng)
      .map(item => ({ lat: item.lat, lng: item.lng }));
  
    const link = generateMultiPinLink(coords);
    navigator.clipboard.writeText(link);
    showSuccessToast("Google Maps link copied to clipboard!");
  };
  
 

  return (
    <div className="bg-white rounded-3xl overflow-hidden h-full flex flex-col">
      <div className="bg-green-50 flex flex-col gap-3 p-4 w-full">
        <div className="flex text-gray-700 items-center justify-between gap-2">
          <div className="flex text-gray-700 items-center gap-2">
            <BsPersonCircle className="text-green-500" />
            <span className="text-sm">{userName || "Unknown User"}</span>
          </div>
          <button onClick={handleShare} className="text-blue-700 bg-white px-3 py-1 flex items-center gap-1 text-sm border rounded-[10px] duration-200">
            <IoShareSocialSharp className="text-lg" /> Share
          </button>
        </div>

        <div className="flex text-nowrap text-gray-700 items-center justify-between gap-2">
          <div className="flex text-gray-700 items-center gap-2">
            <CiCalendar className="text-green-500" />
            <span className="text-sm">{data.date || "N/A"}</span>
          </div>
          <div className="flex text-gray-700 items-center gap-2">
            <GiPathDistance className="text-green-500" />
            <span className="text-sm">{data.totalDistance || "0 km"}</span>
          </div>
          <div className="flex text-gray-700 items-center gap-2">
            <IoTimerOutline className="text-green-500" />
            <span className="text-sm">{data.totalHoursSpend || "0h 0m"}</span>
          </div>
          {data.batteryPercentage && (
            <div className="flex text-gray-700 items-center gap-2">
              <IoBatteryHalfOutline className="text-orange-400" />
              <span className="text-sm">Battery: {data.batteryPercentage}</span>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 mt-2">
        <div className="flex border-b border-gray-200">
          <button
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'timeline'
                ? 'border-b-2 border-blue-500 text-blue-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'property'
                ? 'border-b-2 border-blue-500 text-blue-500'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('property')}
          >
            Property Visits
          </button>
        </div>
      </div>

    {/* Scrollable Content */}
    <div className="h-[calc(120vh-270px)] overflow-y-auto  p-3">
        {activeTab === 'timeline' ? (
          <div className="min-h-0">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-blue-500 font-semibold text-sm">Timeline Details</h3>
              <button
                onClick={downloadTimelineReport}
                className="flex items-center gap-1 text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
              >
                <MdDownload />
                Download Timeline
              </button>
            </div>
            {timelineData.length > 0 ? (
              <div className="min-h-0">
                <Timeline
                  value={timelineData}
                  opposite={customizedOpposite}
                  content={customizedContent}
                  marker={() => (
                    <span className="flex items-center justify-center w-3 h-3 rounded-full bg-customGreen"></span>
                  )}
                  className="w-full custom-timeline"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-3 items-center justify-center h-full">
                <img src={GeoImages.timeLineEmptyIcon} alt="No data" />
                <p className="text-gray-500 text-sm">
                  {selectedProperty
                    ? `No timeline data available for ${selectedProperty.label}`
                    : "No timeline data available"}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="min-h-0">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-blue-500 font-semibold text-sm">Property Summary</h3>
              <button
                onClick={downloadPropertySummaryReport}
                className="flex items-center gap-1 text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 transition-colors"
              >
                <MdDownload />
                Download Report
              </button>
            </div>
            {getPropertySummaryData().length > 0 ? (
              <div className="space-y-3">
                {getPropertySummaryData().map((property, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-4 shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                    onClick={() => {
                      if (property.coordinates && property.coordinates !== 'N/A') {
                        const [lat, lng] = property.coordinates.split(',').map(coord => coord.trim());
                        handlePointClick({
                          lat: parseFloat(lat),
                          lng: parseFloat(lng),
                          action: `View ${property.propertyName}`,
                          time: property.lastVisit,
                          propertyName: property.propertyName,
                          index: index,
                        });
                      }
                    }}
                    title="Click to view property location on map"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800 text-base mb-1">{property.propertyName}</h4>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-gray-600">
                          <span>🕐</span>
                          <span className="font-medium">First Visit:</span>
                        </div>
                        <div className="text-gray-800 font-mono">{property.firstVisit || 'N/A'}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-gray-600">
                          <span>🕐</span>
                          <span className="font-medium">Last Visit:</span>
                        </div>
                        <div className="text-gray-800 font-mono">{property.lastVisit || 'N/A'}</div>
                      </div>
                    </div>
                    {property.location?.shortAddress && property.location.shortAddress !== "Unknown Location" ? (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-start gap-2">
                          <span className="text-gray-400 text-xs mt-0.5">📍</span>
                          <div className="flex-1">
                            <p className="text-xs text-gray-700 font-medium">
                              {property.location.shortAddress}
                            </p>
                            {property.location.fullAddress !== property.location.shortAddress && (
                              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                {property.location.fullAddress.length > 60
                                  ? `${property.location.fullAddress.substring(0, 60)}...`
                                  : property.location.fullAddress}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : property.coordinates && property.coordinates !== 'N/A' ? (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs">📍</span>
                          <p className="text-xs text-gray-500 font-mono">{property.coordinates}</p>
                        </div>
                      </div>
                    ) : null}
                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                      <span className="text-xs text-gray-600">
                        Total Activities: <span className="font-semibold text-blue-600">{property.activities.length}</span>
                      </span>
                      <span className="text-xs text-blue-500 opacity-70">🗺️ Click to view on map</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3 items-center justify-center h-full py-8">
                <img src={GeoImages.timeLineEmptyIcon} alt="No data" className="w-16 h-16 opacity-50" />
                <div className="text-center">
                  <p className="text-gray-500 text-sm font-medium">No property data available</p>
                  <p className="text-gray-400 text-xs mt-1">Property information will appear here when available</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveUserTimeline;